import { Component, inject, signal, computed } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TopicService } from '../../core/services/topic.service';
import { PostService } from '../../core/services/post.service';
import { ContentDraftService } from '../../core/services/content-draft.service';
import { TopicFormComponent } from '../../shared/components/topic-form/topic-form.component';

interface SidebarLink { name: string; href: string; badge?: number | null; divider?: boolean; }
interface SidebarContext { label: string; links: SidebarLink[]; }

@Component({
  selector: 'sw-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CommonModule, TopicFormComponent],
  template: `
    <aside class="sidebar">
      <div class="card glass">

        <div class="ctx-label">{{ context().label }}</div>

        <nav class="nav">
          @for (link of context().links; track link.href) {
            @if (link.divider) {
              <div class="divider"></div>
            }
            <a [routerLink]="link.href" routerLinkActive="active" class="link">
              <span class="link-name">{{ link.name }}</span>
              @if (link.badge) {
                <span class="badge">{{ link.badge }}</span>
              }
              <svg class="arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </a>
          }

          @if (showTopics()) {
            <div class="divider"></div>
            <div class="section-row">
              <span class="section-lbl">Topics</span>
              <button class="add-btn" (click)="showForm.set(true)">+</button>
            </div>
            @for (topic of topicService.topics(); track topic.key) {
              <a [routerLink]="['/topic', topic.key]" routerLinkActive="active" class="link">
                <span class="topic-dot" [style.background]="topic.color"></span>
                <span class="link-name">{{ topic.name }}</span>
                <span class="count">{{ postCountByTopic(topic.key) }}</span>
              </a>
            }
          }
        </nav>

        <div class="footer">
          <span class="pulse-dot"></span>
          <span class="pulse-lbl">Node_Sync: Active</span>
        </div>
      </div>
    </aside>

    @if (showForm()) {
      <sw-topic-form
        (created)="onTopicCreated($event)"
        (cancel)="showForm.set(false)">
      </sw-topic-form>
    }
  `,
  styles: [`
    .sidebar {
      position: fixed;
      left: 0; top: 96px; bottom: 0;
      width: 256px;
      display: none;
      padding: 0 24px 16px;
      z-index: 40;
    }
    @media (min-width: 1024px) { .sidebar { display: block; } }

    .card {
      height: 100%;
      border-radius: 40px;
      display: flex;
      flex-direction: column;
      padding: 20px 12px 16px;
      box-shadow: 0 25px 50px rgba(0,0,0,0.35);
    }

    .ctx-label {
      font-family: var(--font-mono);
      font-size: 10px;
      color: var(--cyan);
      text-transform: uppercase;
      letter-spacing: 0.3em;
      font-weight: 700;
      padding: 8px 16px 20px;
      transition: opacity 0.2s ease;
    }

    .nav { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 1px; }
    .nav::-webkit-scrollbar { width: 0; }

    .link {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 16px;
      border-radius: 16px;
      font-family: var(--font-mono);
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: rgba(255,255,255,0.35);
      text-decoration: none;
      border: 1px solid transparent;
      transition: background 0.2s, color 0.2s, border-color 0.2s;
      position: relative;
    }
    .link:hover { color: rgba(255,255,255,0.75); background: rgba(255,255,255,0.04); }

    ::ng-deep .active.link {
      background: rgba(0,210,255,0.08) !important;
      border-color: rgba(0,210,255,0.18) !important;
      color: var(--white) !important;
    }

    .link-name { flex: 1; }

    .arrow {
      opacity: 0;
      color: rgba(255,255,255,0.2);
      transition: opacity 0.15s, transform 0.15s;
      flex-shrink: 0;
    }
    .link:hover .arrow { opacity: 1; transform: translateX(2px); }
    ::ng-deep .active.link .arrow { opacity: 1; color: var(--cyan); }

    .badge {
      background: var(--cyan);
      color: var(--obsidian);
      font-family: var(--font-mono);
      font-size: 9px;
      font-weight: 700;
      padding: 1px 7px;
      border-radius: 9999px;
      box-shadow: 0 0 8px rgba(0,210,255,0.4);
    }
    .count {
      font-family: var(--font-mono);
      font-size: 9px;
      color: rgba(255,255,255,0.2);
    }

    .topic-dot {
      width: 7px; height: 7px;
      border-radius: 50%; flex-shrink: 0;
    }

    .divider { height: 1px; background: rgba(255,255,255,0.05); margin: 6px 4px 8px; }

    .section-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 16px; margin-bottom: 4px;
    }
    .section-lbl {
      font-family: var(--font-mono);
      font-size: 9px;
      color: rgba(255,255,255,0.2);
      text-transform: uppercase;
      letter-spacing: 0.25em;
    }
    .add-btn {
      background: transparent;
      border: 1px solid rgba(0,210,255,0.2);
      color: rgba(0,210,255,0.6);
      width: 18px; height: 18px;
      border-radius: 4px; font-size: 14px; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.15s var(--ease-spring);
    }
    .add-btn:hover {
      background: rgba(0,210,255,0.1);
      border-color: var(--cyan); color: var(--cyan);
      transform: scale(1.1);
    }

    .footer {
      display: flex; align-items: center; gap: 8px;
      padding: 14px 16px 4px;
      border-top: 1px solid rgba(255,255,255,0.05);
      margin-top: auto;
    }
    .pulse-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--cyan);
      box-shadow: 0 0 6px rgba(0,210,255,0.8);
      animation: pulse-dot 2.5s ease-in-out infinite;
      flex-shrink: 0;
    }
    @keyframes pulse-dot {
      0%, 100% { opacity: 1; box-shadow: 0 0 6px rgba(0,210,255,0.8); }
      50% { opacity: 0.3; box-shadow: 0 0 2px rgba(0,210,255,0.3); }
    }
    .pulse-lbl {
      font-family: var(--font-mono);
      font-size: 8px; color: rgba(255,255,255,0.18);
      text-transform: uppercase; letter-spacing: 0.2em;
    }
  `],
})
export class SidebarComponent {
  readonly topicService        = inject(TopicService);
  readonly postService         = inject(PostService);
  readonly contentDraftService = inject(ContentDraftService);
  readonly router              = inject(Router);
  showForm = signal(false);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  context = computed((): SidebarContext => {
    const url = this.url() ?? '';

    if (url.startsWith('/content')) return {
      label: 'Content // Pipeline',
      links: [
        { name: 'Review Queue', href: '/content/queue',    badge: this.contentDraftService.pendingCount() || null },
        { name: 'Pipeline',     href: '/content/pipeline' },
        { name: 'Sources',      href: '/content/sources'  },
      ],
    };

    if (url.startsWith('/music') || url.startsWith('/brand') || url.startsWith('/export')) return {
      label: 'Studio // Create',
      links: [
        { name: 'Music Library', href: '/music-library' },
        { name: 'Brand Studio',  href: '/brand-studio'  },
        { name: 'Export JSON',   href: '/export'        },
      ],
    };

    if (url.startsWith('/topic')) return {
      label: 'Topics // Brand',
      links: [
        { name: 'All Posts', href: '/posts' },
        { name: 'New Post',  href: '/post/new' },
      ],
    };

    // Default: Posts / Plan
    return {
      label: 'Posts // Feed',
      links: [
        { name: 'Global Plan', href: '/plan',     badge: this.postService.totalCount() || null },
        { name: 'New Post',    href: '/post/new' },
        { name: 'All Posts',   href: '/posts'    },
      ],
    };
  });

  showTopics = computed(() => {
    const url = this.url() ?? '';
    return url.startsWith('/topic') || url.startsWith('/plan') || url.startsWith('/post');
  });

  postCountByTopic(key: string): number {
    return this.postService.posts().filter(p => p.topic_key === key).length;
  }

  onTopicCreated(t: Omit<import('../../core/models/topic.model').Topic, 'active'>) {
    this.topicService.addTopic(t);
    this.showForm.set(false);
  }
}
